import AutoLayerPricing from "./autoLayerPricing/AutoLayerPricing";
import AutoPricing from "./autoPricing/AutoPricing";
import BaseLayerBurn from "./baseLayerBurn/BaseLayerBurn";
import GlPricing from "./glPricing/GlPricing";
import LossRatingPricing from "./lossRating/LossRating";
import Modeling from "./modelData/Modeling";
import XsRatingPricing from "./xsRating/XsRating";
import * as navigationSupport from "components/common/Navigation.support";
import DashboardContent from "components/layout/DashboardContent";
import DashboardHeader from "components/layout/DashboardHeader";
import BurningCost from "components/pricing/burningCost/BurningCost";
import ClaimsSelector from "components/pricing/claimsSelector/ClaimsSelector";
import DnfLayerPricing from "components/pricing/dnfLayerPricing/DnfLayerPricing";
import ExpiringProgram from "components/pricing/expiringProgram/expiringProgram";
import ForestryLayerPricing from "components/pricing/forestryLayerPricing/ForestryLayerPricing";
import IabLayerPricing from "components/pricing/iabLayerPricing/IabLayerPricing";
import LayerPricing from "components/pricing/layerPricing/LayerPricing";
import LossFrequency from "components/pricing/lossFrequency/LossFrequency";
import ModelPricing from "components/pricing/modelPricing/ModelPricing";
import RateAdequacy from "components/pricing/rateAdequacy/RateAdequacy";
import RateChange from "components/pricing/rateChange/RateChange";
import RenewalRateChange from "components/pricing/renewalRateChange/RenewalRateChange";
import TailAnalysis from "components/pricing/tailAnalysis/TailAnalysis";
import TowerPricing from "components/pricing/towerPricing/TowerPricing";
import Ultimates from "components/pricing/ultimates/Ultimates";
import { SPREADSHEET_BASED_PRICING } from "flags";
import { connect } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";
import { useListPricingModelsQuery } from "services/analyticsService";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as persistenceSelectors from "store/selectors/persistence/persistenceSelectors";
import * as userSelectors from "store/selectors/user/userSelectors";

const Pricing = (props) => {
  const {
    orderSubTabs,
    isSubTabOrderLoading,
  } = navigationSupport.useOrderSubTabs({ tabName: "Analysis" });

  const {
    data: pricingModels,
    isLoading: isPricingModelsLoading,
  } = useListPricingModelsQuery(undefined, {
    // Only companies with the flag on can show a model tab, so skipping otherwise avoids a
    // request per Analysis mount and stops a slow or failing call blanking the whole section
    // for the majority of companies that have no workbook models at all.
    skip: !props.componentVisibility[SPREADSHEET_BASED_PRICING],
  });

  // one tab per active workbook-driven model; the tab is bound to its engine and gated on the
  // same componentVisibility mechanism as every built-in tab, so a model can be staged per
  // company rather than appearing for everyone the moment its registry entry goes active
  const modelTabs = (pricingModels?.values?.models ?? [])
    .filter((model) => model?.engine)
    .map((model) => ({
      title: model.title || model.engine,
      path: model.engine,
      element: <ModelPricing engine={model.engine} title={model.title} />,
      visibilityTag: SPREADSHEET_BASED_PRICING,
      defaultVisibility: false,
      isModelTab: true,
    }));

  // wait for the model list too, otherwise a deep link to a model tab is
  // redirected away by the catch-all route before its Route exists
  if (isSubTabOrderLoading || isPricingModelsLoading) {
    return <></>;
  }

  const tabs = [
    {
      title: "Tail Analysis",
      path: "tail_analysis",
      element: <TailAnalysis />,
      visibilityTag: "pricing.tailAnalysis",
      defaultVisibility: false,
    },
    {
      title: "Claims",
      path: "claims",
      element: <ClaimsSelector />,
      visibilityTag: "pricing.claimsSelector",
      defaultVisibility: false,
    },
    {
      title: "Loss Frequency",
      path: "loss_frequency",
      element: <LossFrequency />,
      visibilityTag: "pricing.lossFrequency",
      defaultVisibility: true,
    },
    {
      title: "Ultimate",
      path: "ultimate",
      element: <Ultimates />,
      visibilityTag: "pricing.ultimate",
      defaultVisibility: false,
    },
    {
      title: "Burning Cost",
      path: "burning_cost",
      element: <BurningCost />,
      visibilityTag: "pricing.burningCost",
      defaultVisibility: false,
    },
    {
      title: "Modeling",
      path: "modeling",
      element: <Modeling />,
      visibilityTag: "pricing.modeling",
      defaultVisibility: false,
    },
    {
      title: "Base Layer Burn",
      path: "base_layer_burn",
      element: <BaseLayerBurn />,
      visibilityTag: "pricing.baseLayerBurn",
      defaultVisibility: false,
    },
    {
      title: "GL Pricing",
      path: "gl_pricing",
      element: <GlPricing />,
      visibilityTag: "pricing.glPricing",
      defaultVisibility: false,
    },
    ...modelTabs,
    {
      title: "Auto Pricing",
      path: "auto_pricing",
      element: <AutoPricing />,
      visibilityTag: "pricing.autoPricing",
      defaultVisibility: false,
    },
    {
      title: "Loss Rating",
      path: "loss_rating",
      element: <LossRatingPricing />,
      visibilityTag: "pricing.lossRating",
      defaultVisibility: false,
    },
    {
      title: "XS Rating",
      path: "xs_rating",
      element: <XsRatingPricing />,
      visibilityTag: "pricing.xsRating",
      defaultVisibility: false,
    },
    {
      title: "Layer Pricing",
      path: "layer_pricing",
      element: <LayerPricing />,
      visibilityTag: "pricing.layerPricing",
      defaultVisibility: true,
    },
    {
      title: "D&F Layer Pricing",
      path: "dnf_layer_pricing",
      element: <DnfLayerPricing />,
      visibilityTag: "pricing.dnfLayerPricing",
      defaultVisibility: false,
    },
    {
      title: "Forestry Layer Pricing",
      path: "forestry_layer_pricing",
      element: <ForestryLayerPricing />,
      visibilityTag: "pricing.forestryLayerPricing",
      defaultVisibility: false,
    },
    {
      title: "IAB Layer Pricing",
      path: "iab_layer_pricing",
      element: <IabLayerPricing />,
      visibilityTag: "pricing.iabLayerPricing",
      defaultVisibility: false,
    },
    {
      title: "Auto Layer Pricing",
      path: "auto_layer_pricing",
      element: <AutoLayerPricing />,
      visibilityTag: "pricing.autoLayerPricing",
      defaultVisibility: false,
    },
    {
      title: "Tower Pricing",
      path: "tower_pricing",
      element: <TowerPricing />,
      visibilityTag: "pricing.towerPricing",
      defaultVisibility: true,
    },
    {
      title: "Expiring Program",
      path: "expiring_program",
      element: <ExpiringProgram />,
      visibilityTag: "pricing.expiringProgram",
      defaultVisibility: true,
    },
    {
      title: "Rate Change",
      path: "rate_change",
      element: <RateChange />,
      hidden: props.hasSubmission && !props.hasPriorSubmission,
      visibilityTag: "pricing.rateChange",
      defaultVisibility: true,
    },
    {
      title: "Rate Adequacy",
      path: "rate_adequacy",
      element: <RateAdequacy />,
      visibilityTag: "pricing.rateAdequacy",
      defaultVisibility: false,
    },
    {
      title: "Renewal Rate Change",
      path: "renewal_rate_change",
      element: <RenewalRateChange />,
      visibilityTag: "pricing.renewalRateChange",
      defaultVisibility: false,
    },
  ]
    .filter((x) => !x.hidden)
    .filter(
      (x) => props.componentVisibility[x.visibilityTag] ?? x.defaultVisibility
    );

  // a model tab must never shadow a built-in tab: paths collide into duplicate
  // routes, and titles collide in orderSubTabs (which matches on title)
  const builtInPaths = new Set(
    tabs.filter((tab) => !tab.isModelTab).map((tab) => tab.path)
  );
  const builtInTitles = new Set(
    tabs.filter((tab) => !tab.isModelTab).map((tab) => tab.title)
  );
  const uniqueTabs = tabs.filter(
    (tab, index, all) =>
      !(
        tab.isModelTab &&
        (builtInPaths.has(tab.path) || builtInTitles.has(tab.title))
      ) &&
      all.findIndex((other) => other.path === tab.path) === index &&
      all.findIndex((other) => other.title === tab.title) === index
  );

  const orderedTabs = orderSubTabs(uniqueTabs);

  return (
    <>
      <DashboardHeader
        tabs={orderedTabs}
        parentPath={"pricing"}
        navigationState={props.navigationState}
        setNavigationState={props.setNavigationState}
        isFullWidth={props.isFullWidth}
        sticky={props.hasStickyHeaders}
      />
      <DashboardContent isFullWidth={props.isFullWidth}>
        <Routes>
          {orderedTabs.map(({ element, path }) => (
            <Route key={path} path={`${path}/*`} element={element} />
          ))}
          <Route
            path={"*"}
            element={
              <Navigate
                to={
                  props.navigationState?.pricing != null &&
                  orderedTabs.some(
                    (tab) => tab.path === props.navigationState.pricing
                  )
                    ? props.navigationState.pricing
                    : orderedTabs[0].path
                }
                replace={true}
              />
            }
          />
        </Routes>
      </DashboardContent>
    </>
  );
};

export const mapStateToProps = (state) => ({
  componentVisibility:
    userSelectors.selectUserConfig(state).componentVisibility || {},
  hasSubmission: persistenceSelectors.selectFilename(state) != null,
  hasPriorSubmission: programSelectors.getPriorSubmission(state) != null,
});

export default connect(mapStateToProps)(Pricing);
